import { useState, useEffect, useRef } from 'react';
import { loadPrefs, savePrefs } from '../lib/prefs';
import { IconTile } from './Icons';

const GROUPS = [
  ['Día a día', ['dashboard', 'records', 'extra_hours', 'alertas']],
  ['Personas', ['employees', 'vacaciones', 'licencias', 'excepciones', 'tareas', 'holidays']],
  ['Dinero', ['payroll']],
  ['Análisis y ajustes', ['analysis', 'settings']],
];
const read = (k, d) => { try { const v = JSON.parse(localStorage.getItem(k)); return v ?? d; } catch { return d; } };
const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* sin storage */ } };

// Tiles de inicio agrupados. El administrador puede fijar favoritos y reordenar (se guarda en este dispositivo).
export default function HomeTiles({ tabs, labels = {}, badges = {}, onOpen, userId }) {
  const kp = 'g360_pins_' + userId, ko = 'g360_order_' + userId;
  const [pins, setPins] = useState(() => read(kp, []));
  const [order, setOrder] = useState(() => read(ko, []));
  const [edit, setEdit] = useState(false);
  const [syncErr, setSyncErr] = useState(false);
  const timer = useRef(null);

  // El servidor manda: al abrir carga las preferencias de esta cuenta (la copia local es solo un respaldo rápido).
  useEffect(() => {
    let alive = true;
    loadPrefs(userId).then(p => {
      if (!alive || !p.home_tiles) return;
      setPins(p.home_tiles.pins || []); setOrder(p.home_tiles.order || []);
      write(kp, p.home_tiles.pins || []); write(ko, p.home_tiles.order || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, [userId, kp, ko]);

  const persist = (nextPins, nextOrder) => {
    write(kp, nextPins); write(ko, nextOrder);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      savePrefs(userId, { home_tiles: { pins: nextPins, order: nextOrder } }).then(() => setSyncErr(false)).catch(() => setSyncErr(true));
    }, 500);
  };

  const ids = tabs.map(t => t[0]);
  const rank = id => { const i = order.indexOf(id); return i < 0 ? 1000 + ids.indexOf(id) : i; };
  const sortIds = list => [...list].sort((a, b) => rank(a) - rank(b));
  const labelOf = id => labels[id] || tabs.find(t => t[0] === id)?.[1] || id;

  const pinned = sortIds(pins.filter(id => ids.includes(id)));
  const groups = GROUPS.map(([name, g]) => [name, sortIds(g.filter(id => ids.includes(id) && !pinned.includes(id)))]);
  const known = GROUPS.flatMap(g => g[1]);
  const other = sortIds(ids.filter(id => !known.includes(id) && !pinned.includes(id)));
  if (other.length) groups.push(['Otros', other]);

  const togglePin = id => { const n = pins.includes(id) ? pins.filter(x => x !== id) : [...pins, id]; setPins(n); persist(n, order); };
  const move = (list, id, dir) => {
    const i = list.indexOf(id), j = i + dir;
    if (j < 0 || j >= list.length) return;
    const l = [...list]; [l[i], l[j]] = [l[j], l[i]];
    const rest = order.filter(x => !l.includes(x));
    const n = [...l, ...rest]; setOrder(n); persist(pins, n);
  };
  const reset = () => { setPins([]); setOrder([]); persist([], []); };

  // Los controles van FUERA del botón: dentro de un <button disabled> los clics no llegan.
  const renderTile = (id, list) => (
    <div key={id} className={`relative flex flex-col rounded-2xl border bg-white transition-all ${edit ? 'border-sky-200' : 'border-gray-100 hover:border-sky-200 hover:shadow-sm active:scale-95'}`}>
      <button type="button" onClick={() => !edit && onOpen(id)}
        className="relative flex flex-col items-center justify-start gap-1.5 pt-3 pb-2.5 px-1 w-full">
        {badges[id] > 0 && <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{badges[id]}</span>}
        <IconTile name={id} />
        <span className="text-[11px] font-semibold text-gray-600 text-center leading-tight">{labelOf(id)}</span>
      </button>
      {edit && (
        <div className="flex items-center justify-center gap-1 pb-2">
          <button type="button" onClick={() => move(list, id, -1)} aria-label="Mover antes" className="w-7 h-7 rounded-lg bg-gray-100 text-gray-600 text-xs flex items-center justify-center active:scale-90">◀</button>
          <button type="button" onClick={() => togglePin(id)} aria-label="Favorito" className={`w-7 h-7 rounded-lg text-sm flex items-center justify-center active:scale-90 ${pins.includes(id) ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-400'}`}>★</button>
          <button type="button" onClick={() => move(list, id, 1)} aria-label="Mover después" className="w-7 h-7 rounded-lg bg-gray-100 text-gray-600 text-xs flex items-center justify-center active:scale-90">▶</button>
        </div>
      )}
    </div>
  );
  const renderGrid = (list) => <div className="grid grid-cols-4 sm:grid-cols-6 gap-2.5">{list.map(id => renderTile(id, list))}</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">{syncErr ? 'No se pudo guardar tu orden en el servidor; reintentá.' : edit ? 'Tocá ★ para fijar favoritos y ◀ ▶ para mover. Se guarda en tu cuenta.' : 'Tus accesos, agrupados.'}</p>
        <div className="flex gap-2">
          {edit && <button onClick={reset} className="text-xs font-semibold text-gray-400">Restablecer</button>}
          <button onClick={() => setEdit(!edit)} className={`text-xs font-bold px-3 py-1.5 rounded-xl ${edit ? 'bg-sky-700 text-white' : 'bg-gray-100 text-gray-600'}`}>{edit ? 'Listo' : 'Ordenar'}</button>
        </div>
      </div>
      {pinned.length > 0 && (
        <section><p className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-2">★ Favoritos</p>{renderGrid(pinned)}</section>
      )}
      {groups.filter(([, l]) => l.length).map(([name, l]) => (
        <section key={name}><p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">{name}</p>{renderGrid(l)}</section>
      ))}
    </div>
  );
}
